/*
    Axios Service – configuração global com interceptors (Expo/React Native)
  - Anexa token de acesso automaticamente
  - Tenta refresh de token em 401 (com fila/lock para evitar corridas)
  - Retry simples para falhas transitórias (GET, 5xx/timeout)
  - Normaliza erros
*/

import axios, { AxiosError, type AxiosInstance } from "axios";

// typings
import type { NormalizedHttpError, RequestConfig } from "@/data/http/axios/interface";
// import { TokenStorage } from '@/data/infra/database/async-storage';
import { TokenStorage } from "@/data/infra/cache/async-storage";
// import { AppError } from '@/domain/errors/app-error';
import { AppError } from "@/domain/errors/app-error";
import { BASE_API as API_BASE_URL } from "@/environments";
import { handleNormalizeApiError } from "@/utils/normalize-api-error/normalize-api-error";

const REFRESH_PATH = "/auth/refresh";

export class ApiService {
  private static instance: ApiService;

  public readonly client: AxiosInstance; // cliente com interceptors
  private readonly bare: AxiosInstance; // cliente sem interceptors (para o refresh)

  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;

  private onUnauthorized?: () => void; // callback opcional p/ deslogar

  private constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 15000,
    });

    this.bare = axios.create({
      baseURL: API_BASE_URL,
      timeout: 15000,
    });

    this.setupInterceptors();
  }

  public static getInstance() {
    if (!ApiService.instance) ApiService.instance = new ApiService();
    return ApiService.instance;
  }

  /** Permite registrar um callback para lidar com sessão inválida */
  public setUnauthorizedHandler(cb: () => void) {
    this.onUnauthorized = cb;
  }

  /** Permite setar tokens manualmente (ex.: após login) */
  public async setAuthTokens(access?: string, refresh?: string) {
    await TokenStorage.setTokens(access ?? null, refresh ?? null);
  }

  private setupInterceptors() {
    // REQUEST: anexa Authorization
    this.client.interceptors.request.use(async (config) => {
      const token = await TokenStorage.getAccessToken();
      if (token) {
        config.headers = config.headers ?? null;
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      // headers padrão (não sobrescreve Content-Type se já estiver definido, ex: multipart/form-data)
      // 📥 Accept (só adiciona se não existir)
      if (!config.headers["Accept"]) {
        config.headers["Accept"] = "application/json";
      }

      return config;
    });

    // RESPONSE: trata erros, refresh e retry
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const original = (error.config || {}) as RequestConfig;
        const status = error.response?.status;
        const data = error.response?.data as {
          message?: string;
          code?: string;
          errors?: Record<string, unknown>;
          error?: string;
          detail?: string;
        };

        // 401 → refresh
        if (status === 401 && !original._retry) {
          original._retry = true;
          const newToken = await this.refreshTokensSafe();

          if (newToken) {
            original.headers = original.headers ?? {};
            original.headers["Authorization"] = `Bearer ${newToken}`;
            return this.client.request(original);
          }

          await TokenStorage.clear();
          this.onUnauthorized?.();

          throw new AppError(data?.message || "Sessão expirada", 401, 401, data?.code, data);
        }

        // erro de rede / timeout
        if (!error.response) {
          throw new AppError("Erro de conexão com o servidor", undefined, 500, "NETWORK_ERROR");
        }

        // erro HTTP normal
        const message =
          status === 400 || status === 422
            ? handleNormalizeApiError(data)
            : data?.message || data?.error || data?.detail || "Erro inesperado";

        throw new AppError(message, status, 500, data?.code, data);
      }
    );
  }

  /** Refresh com lock + fila: garante apenas um refresh concorrente */
  private async refreshTokensSafe(): Promise<string | null> {
    if (this.isRefreshing && this.refreshPromise) return this.refreshPromise;

    this.isRefreshing = true;
    //@ts-expect-error
    this.refreshPromise = (async () => {
      try {
        const refresh = await TokenStorage.getRefreshToken();
        if (!refresh) return null;

        const res = await this.bare.post(REFRESH_PATH, {
          refresh_token: refresh,
        });
        const access_token = res.data?.access_token as string | undefined;
        const refresh_token = res.data?.refresh_token as string | undefined;

        if (!access_token) return null;
        await TokenStorage.setTokens(access_token, refresh_token ?? null);
        return access_token;
      } catch (error) {
        return error;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /** Normaliza erros para consumo nas telas */
  public normalizeError(err: unknown): NormalizedHttpError {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      const data = err.response?.data;
      const messageFromApi = data?.message || data?.error || data?.detail;
      return {
        status,
        code: (err.code || undefined)?.toLowerCase(),
        message: messageFromApi || err.message || "Erro de requisição",
        details: data,
      };
    }
    if (err instanceof AppError) {
      return {
        status: err.statusCode,
        message: err.message,
        details: err.responseData,
      };
    }
    return { message: "Erro desconhecido" };
  }
}
